import React, { useContext, useEffect, useState } from 'react';

import { Provider, ProviderFilters } from '../../app/models/Provider';
import useProviderService from '../../app/services/hooks/useProviderService';
import { toast } from 'react-toastify';
import ProviderCreate from './components/ProviderCreate';
import ProviderEdit from './components/ProviderEdit';
import ProviderDataTable from './components/ProviderDataTable';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import { AppContext } from '../../contexts/AppContext';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import DefaultModal from '../../components/default/DefaultModal';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Proveedores',
        url: '/Providers',
        isActive: true
    }
];

const Providers = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetProviders, getProviders, deleteProvider, activeProvider } =
        useProviderService();
    const [providers, setProviders] = useState<Provider[]>([]);

    const [providerIdEdit, setProviderIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const { navigationPage } = useNavigationPage();

    const [filter, setFilter] = useState<ProviderFilters>({
        // provider_id: null,
        // rut: '',
        // name: '',
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getProviders(filter, {
            onSuccess: (response: ServiceResponse) => {
                setProviders(response.data.providers);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (ProviderId: number) => {
        setShowingEdit(true);
        setProviderIdEdit(ProviderId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setProviderIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const onError = () => {
        setShowingEdit(false);
        setProviderIdEdit(-1);
    };

    const destroy = (ProviderId: number) => {
        const _text = 'Está a punto de eliminar el proveedor #' + ProviderId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando proveedor...');
                        deleteProvider(ProviderId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El proveedor no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (providerId: number, active: boolean) => {
        activeProvider(providerId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Proveedores" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo proveedor" />}
                />

                <ProviderDataTable
                    providers={providers}
                    totalRows={totalRows}
                    loading={fetchingGetProviders}
                    destroy={destroy}
                    edit={showEdit}
                    active={active}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear proveedor"
                    backdrop={true}
                    showFooter={false}
                >
                    <ProviderCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Proveedor"
                    backdrop={true}
                    showFooter={false}
                >
                    <ProviderEdit
                        providerId={providerIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Providers;
